import { GeocoderType, LayerConfig } from "../ts-types/mapsTypes";
import { OverlayLayer } from "./action-bar-constants";
import type { Color } from "@deck.gl/core";

export const NHC_TROPICAL_WEATHER_LAYER_IDS = (() => {
  const outlookLayers = [1, 2, 3, 398, 399];
  const stormBases = [
    4, 30, 56, 82, 108, 134, 160, 186, 212, 238, 264, 290, 316, 342, 368,
  ];
  const perStormOffsets = [2, 3, 4, 5];
  const stormLayers = stormBases.flatMap((base) =>
    perStormOffsets.map((offset) => base + offset),
  );
  return [...outlookLayers, ...stormLayers, ...[395, 396, 397]].join(",");
})();

export const GOOGLE_EARTH_ALTITUDE = 400;
export const DEFAULT_LAT = 38.0916896;
export const DEFAULT_LNG = -91.453049;
export const DEFAULT_ZOOM = 5.65;
export const DEFAULT_CENTER: [number, number] = [-94.5786, 39.0997];

export const SOURCES = {
  REGEOCODE: "REGEOCODE",
  CAPE_ANALYTICS: "CA",
  BETTERVIEW: "BV",
  BING_GEOCODING: "BG",
  DISTANCE_TO_COAST: "DTC",
  ESRI_GEOCODING: "EG",
  E2VALUE_COMM_LITE: "E2V",
  E2VALUE_RES_LITE: "E2VR",
  GOOGLE_GEOCODING: "GG",
  GOOGLE_ADDRESS_VALIDATION: "GAV",
  GEOCODIO: "GIO",
  AZURE_GEOCODING: "AZG",
  HAZARDHUB: "HH",
  KATRISK: "KR",
  LIBPOSTAL: "LP",
  LIGHTBOX: "LB",
  LIGHTBOX_PARCEL: "LBP",
  LIGHTBOX_GEOCODING: "LBG",
  LIGHTBOX_FLOODZONE: "LBFZ",
  LIGHTBOX_SPATIAL_STREAM: "LBSS",
  LIGHTBOX_REVERSE_GEOCODING: "LBGR",
  MESSYDATA: "MD",
  PING_GEOCODING: "PG",
  PING_GEOCODING_BETA: "PGB",
  PING_SLOSH_ZONE: "PSZ",
  PING_FEMA_FLOOD_ZONE: "PFF",
  PING_USDA_WILDFIRE: "PUSDAW",
  USDA_ENHANCED_RPS: "USDAERPS",
  FEMA_NATIONAL_RISK_INDEX: "FNRI",
  PRECISELY_GEOCODING: "PSG",
  NOOP: "NOOP",
  QUANTARIUM: "Q",
  TEREN4D: "T4D",
  TIGERRISKAIR: "TRAIR",
  TIGERRISKRMS: "TRRMS",
  VERISK_PROPERTY: "VP",
  DISTANCE_TO_SINKHOLE: "DTFS",
  REDZONE: "RZR",
  RMS_LOSS_PRICING: "RMSLCP",
  RMS_US_EXPOSURE: "RMS_US_ESDB",
  INTERMAPS: "IMF",
  GOOGLE_ELEVATION: "GEV",
  EASI_CRIME: "EC",
  VEXCEL_PROPERTY_INFORMATION: "VPI",
  VEXCEL_PROPERTY_DAMAGE: "VPD",
  TRUE_FLOOD_RISK_FFH: "TFR_FFH",
  E2VALUE: "E2V",
  SWISSRE_STORMSURGE: "SW_SS",
  SWISSRE_WINDSTORM: "SW_WS",
  SWISSRE_LIGHTNING: "SW_LT",
  SWISSRE_HAILSTORM: "SW_HS",
  SWISSRE_WILDFIRE: "SW_WF",
  SWISSRE_TORNADO: "SW_TD",
  SWISSRE_EARTHQUAKE: "SW_EQ",
  SWISSRE_TSUNAMI: "SW_TSNMI",
  AON_CS_EQ: "AON_CS_EQ",
  AON_CS_FLD: "AON_CS_FLD",
  AON_CS_SCS: "AON_CS_SCS",
  AON_CS_WF: "AON_CS_WF",
  FIRST_STREET_WILDFIRE: "FS_WF",
  GUY_CARP_HAZARD: "GC_HZ",
  USGS_WBD: "USGS_WBD",
  PING_VALUATION: "PVAL",
  PING_PERMITS: "PP",

  // Ping Hazard is its own source now
  PING_HAZARD: "PH",
  PING_HAZARD_V2: "PH2",

  // WKFC Sources
  RYAN_GEOCODING: "R_G",
  RYAN_KATRISK: "R_KR",
  RYAN_HAZARD_V3: "R_H_V3",
  RYAN_PRECISELY_WILDFIRE: "R_P_W",
  RYAN_NEARMAP: "R_N",

  // Property Guardian Sources
  GREEN_SHIELD_WILDFIRE_RECURRENCE_SCORE: "GS_WF_RS",
  GREEN_SHIELD_RESILIENCY_SCORE: "GS_RS",
  GREEN_SHIELD_ALL: "GS_ALL",

  // Sources for appending additional sources, not actual sources
  PLI_OUTLINES: "PLI_OUTLINES",
  PLI_DATA: "PLI_DATA",
  VEXCEL_DATA: "VEXCEL_DATA",
  SWISSRE_ALL: "SWISSRE_ALL",
  AON_ALL: "AON_ALL",
};

export const PING_CONST_TO_RMS_MAP = [
  {
    label: "N/A",
    value: "",
  },
  {
    label: "Concrete - Tilt-Up",
    value: "3B4",
  },
  {
    label: "Concrete - Reinforced Concrete",
    value: "3",
  },
  {
    label: "Masonry - Masonry & Joisted Masonry",
    value: "2",
  },
  {
    label: "Steel - Light Metal",
    value: "4B",
  },
  {
    label: "Steel - Steel",
    value: "4",
  },
  {
    label: "Masonry - Reinforced Masonry",
    value: "2C",
  },
  {
    label: "Concrete - Pre-cast Concrete",
    value: "3B",
  },
  {
    label: "Wood - Wood Frame (Modern)",
    value: "1",
  },
];

export const PING_OCCUPANCY_TO_RMS_MAP = [
  {
    label: "N/A",
    value: "",
  },
  {
    label: "Commercial - Wholesale Trade",
    value: "6",
  },
  {
    label: "Commercial - Retail Trade",
    value: "5",
  },
  {
    label: "Industrial - General Industrial",
    value: "38",
  },
  {
    label: "Commercial - General Commercial",
    value: "37",
  },
  {
    label: "Industrial - Light Fabrication and Assembly",
    value: "13",
  },
  {
    label: "Commercial - Health Care Services",
    value: "9",
  },
  {
    label: "Commercial - Professional, Technical, and Business Services",
    value: "8",
  },
  {
    label: "Residential - Permanent Dwelling: Single-Family",
    value: "1",
  },
  {
    label: "Commercial - Entertainment and Recreation",
    value: "10",
  },
  {
    label: "Residential - Apartments/Condominiums & General Residential",
    value: "42",
  },
  {
    label: "Commercial - Parking",
    value: "11",
  },
  {
    label: "Residential - Temporary Lodging",
    value: "3",
  },
  {
    label: "Transportation - Highway",
    value: "26",
  },
  {
    label: "Commercial - Personal and Repair Services",
    value: "7",
  },
  {
    label: "Utilities - Water Utilities",
    value: "31",
  },
  {
    label: "Utilities - Sanitary Sewer",
    value: "32",
  },
];

export const PING_DEDUCTIBLE_OPTIONS = [
  { label: "%1", value: 0.01 },
  { label: "%3", value: 0.03 },
  { label: "%5", value: 0.05 },
];

export const AOA_SECTION_MAP = {
  [SOURCES.TIGERRISKAIR]: {
    label: "TigerCQ AIR",
    buttonLabel: "Generate AALs",
    aoaFields: [
      {
        label: "Building Limit",
        placeholder: "Enter Building Limit",
        fieldName: "limits__building_limit",
        isRequired: true,
        inputType: "input",
      },
    ],
  },
  [SOURCES.TIGERRISKRMS]: {
    label: "TigerCQ RMS",
    buttonLabel: "Generate AALs",
    aoaFields: [
      {
        label: "Building Limit",
        placeholder: "Enter Building Limit",
        fieldName: "limits__building_limit",
        isRequired: true,
        inputType: "input",
      },
    ],
  },
  [SOURCES.RMS_LOSS_PRICING]: {
    label: "Moody's Peril Metrics",
    buttonLabel: "Generate AALs",
    aoaFields: [
      {
        label: "Year Built",
        placeholder: "Enter Year Built",
        fieldName: "const__bldg_year_built",
        isRequired: true,
        inputType: "input",
      },
      {
        label: "Building Area",
        placeholder: "Enter Building Area",
        fieldName: "const__bldg_area",
        isRequired: true,
        inputType: "input",
      },
      {
        label: "Building Limit",
        placeholder: "Enter Building Limit",
        fieldName: "limits__building_limit",
        isRequired: true,
        inputType: "input",
      },
      {
        label: "BPP Limit",
        placeholder: "Enter BPP Limit",
        fieldName: "limits__bpp_limit",
        isRequired: false,
        inputType: "input",
      },
      {
        label: "BI Limit",
        placeholder: "Enter BI Limit",
        fieldName: "limits__bi_limit",
        isRequired: false,
        inputType: "input",
      },
      {
        label: "Construction",
        placeholder: "Enter Construction",
        fieldName: "const__code_rms",
        isRequired: false,
        inputType: "select",
        options: {
          selectOptions: PING_CONST_TO_RMS_MAP,
        },
      },
      {
        label: "Occupancy",
        placeholder: "Enter Occupancy",
        fieldName: "occupancy__code_atc",
        isRequired: false,
        inputType: "select",
        options: {
          selectOptions: PING_OCCUPANCY_TO_RMS_MAP,
        },
      },
      {
        label: "Deductible Amount",
        placeholder: "Enter Deductible Amount",
        fieldName: "deductible_amount",
        isRequired: false,
        isAlwaysShow: true,
        inputType: "select",
        options: {
          selectOptions: PING_DEDUCTIBLE_OPTIONS,
        },
        defaultValue: 0.05,
      },
    ],
  },
  [SOURCES.AON_ALL]: {
    label: "AON CatScore",
    buttonLabel: "Enrich",
  },
  [SOURCES.GREEN_SHIELD_ALL]: {
    label: "Property Guardian Wildfire",
    buttonLabel: "Enrich",
    aoaFields: [],
  },
  [SOURCES.AON_CS_EQ]: {
    label: "AON CatScore EQ",
    buttonLabel: "Enrich",
    aoaFields: [],
  },
  [SOURCES.AON_CS_FLD]: {
    label: "AON CatScore Flood",
    buttonLabel: "Enrich",
    aoaFields: [],
  },
  [SOURCES.AON_CS_SCS]: {
    label: "AON CatScore SCS",
    buttonLabel: "Enrich",
    aoaFields: [],
  },
  [SOURCES.AON_CS_WF]: {
    label: "AON CatScore Wildfire",
    buttonLabel: "Enrich",
    aoaFields: [],
  },
  [SOURCES.FIRST_STREET_WILDFIRE]: {
    label: "First Street Fire Factor",
    buttonLabel: "Enrich",
    aoaFields: [],
  },
  [SOURCES.GUY_CARP_HAZARD]: {
    label: "Guy Carpenter Hazard",
    buttonLabel: "Enrich",
    aoaFields: [],
  },
  [SOURCES.INTERMAPS]: {
    label: "Intermaps",
    buttonLabel: "Enrich",
    aoaFields: [],
  },
  [SOURCES.HAZARDHUB]: {
    label: "HazardHub",
    buttonLabel: "Enrich",
    aoaFields: [],
  },
  [SOURCES.PING_PERMITS]: {
    label: "Ping Permits",
    buttonLabel: "Enrich",
    aoaFields: [],
  },
  [SOURCES.PING_HAZARD]: {
    label: "Ping Hazard",
    buttonLabel: "Enrich",
    aoaFields: [],
  },
  [SOURCES.RMS_US_EXPOSURE]: {
    label: "Moody's US Exposure",
    buttonLabel: "Enrich",
    aoaFields: [],
  },
  [SOURCES.RYAN_KATRISK]: {
    label: "KatRisk",
    buttonLabel: "Enrich",
    aoaFields: [],
  },
  [SOURCES.RYAN_HAZARD_V3]: {
    label: "CoreLogic",
    buttonLabel: "Enrich",
    aoaFields: [],
  },
  [SOURCES.RYAN_PRECISELY_WILDFIRE]: {
    label: "Precisely Wildfire",
    buttonLabel: "Enrich",
    aoaFields: [],
  },
  [SOURCES.RYAN_NEARMAP]: {
    label: "NearMap",
    buttonLabel: "Enrich",
    aoaFields: [],
  },
  [SOURCES.CAPE_ANALYTICS]: {
    label: "Moody's CAPE Analytics",
    buttonLabel: "Enrich",
    aoaFields: [],
  },
  [SOURCES.VEXCEL_PROPERTY_INFORMATION]: {
    label: "Vexcel Elements AI: Property & Building Attributes",
    buttonLabel: "Enrich",
    aoaFields: [],
  },
  [SOURCES.VEXCEL_PROPERTY_DAMAGE]: {
    label: "Vexcel Elements AI: Damage Assessment",
    buttonLabel: "Enrich",
    aoaFields: [],
  },
  [SOURCES.TRUE_FLOOD_RISK_FFH]: {
    label: "True Flood Risk",
    buttonLabel: "Enrich",
    aoaFields: [],
  },

  [SOURCES.USDA_ENHANCED_RPS]: {
    label: "USDA Enhanced Risk to Structures",
    buttonLabel: "Enrich",
    aoaFields: [],
  },
  [SOURCES.PING_VALUATION]: {
    label: "Ping Valuation",
    buttonLabel: "Enrich",
    isBeta: true,
    aoaFields: [
      {
        label: "Building Area",
        placeholder: "Enter Building Area",
        fieldName: "const__bldg_area",
        isRequired: true,
        isAlwaysShow: true,
        inputType: "input",
      },
      {
        label: "# of Stories",
        placeholder: "Enter # of Stories",
        fieldName: "const__num_stories",
        isRequired: true,
        isAlwaysShow: true,
        inputType: "input",
      },
      {
        label: "AIR Construction Code",
        placeholder: "Enter AIR Construction Code",
        fieldName: "const__code_air",
        isRequired: true,
        isAlwaysShow: true,
        inputType: "input",
        defaultValue: 101,
      },
      {
        label: "AIR Occupancy Code",
        placeholder: "Enter AIR Occupancy Code",
        fieldName: "occupancy__code_air",
        isRequired: true,
        isAlwaysShow: true,
        inputType: "input",
      },
    ],
  },
  [SOURCES.E2VALUE]: {
    label: "e2Value ProntoLite Commercial",
    buttonLabel: "Enrich",
    aoaFields: [
      {
        label: "Construction",
        placeholder: "Select Construction",
        fieldName: "const__desc_ping",
        isRequired: true,
        isAlwaysShow: true,
        inputType: "select",
      },
      {
        label: "Occupancy",
        placeholder: "Select Occupancy",
        fieldName: "occupancy__desc_ping",
        isRequired: true,
        isAlwaysShow: true,
        inputType: "select",
      },
      {
        label: "Square Footage",
        placeholder: "Enter Square Footage",
        fieldName: "const__bldg_area",
        isRequired: true,
        isAlwaysShow: true,
        inputType: "input",
      },
    ],
  },
  [SOURCES.SWISSRE_ALL]: {
    label: "Swiss Re CatNet",
    buttonLabel: "Enrich",
  },
  [SOURCES.GREEN_SHIELD_WILDFIRE_RECURRENCE_SCORE]: {
    label: "Property Guardian - Recurrence Score",
    buttonLabel: "Enrich",
    aoaFields: [],
  },
  [SOURCES.GREEN_SHIELD_RESILIENCY_SCORE]: {
    label: "Property Guardian - Resiliency Score",
    buttonLabel: "Enrich",
    aoaFields: [],
  },
};

export const SQFT_PER_SQMILE = 27878400;
export const LAT_LNG_MOVED_THRESHOLD = 0.0001;
export const REGEOCODING_LAT_LNG_MOVED_THRESHOLD = 0.01;
export const BLDG_LOOKUP_LAT_LNG_MOVED_THRESHOLD = 0.00005;
export const RADIUS_CHANGE_THRESHOLD = 1;
export const AREA_MEASUREMENT_TOOL_SNAPSHOT_THRESHOLD = 20;
export const SQM_TO_SQFT = 10.76391;
export const MT_TO_FT = 3.28084;
export const MT_TO_MILE = 0.000621371;
export const MILE_TO_FT = 5280;
export const PERCENTAGE_THRESHOLD = 0.7;

const PING_LOOKUP = {
  ak: {
    "aleutians east": {
      wind_tier: null,
      eq_zone: "AK",
      eq_sub_zone: null,
    },
    "aleutians east borough": {
      wind_tier: null,
      eq_zone: "AK",
      eq_sub_zone: null,
    },
    "aleutians west": {
      wind_tier: null,
      eq_zone: "AK",
      eq_sub_zone: null,
    },
    "aleutians west census area": {
      wind_tier: null,
      eq_zone: "AK",
      eq_sub_zone: null,
    },
    anchorage: { wind_tier: null, eq_zone: "AK", eq_sub_zone: null },
    "anchorage municipality": {
      wind_tier: null,
      eq_zone: "AK",
      eq_sub_zone: null,
    },
    bethel: { wind_tier: null, eq_zone: "AK", eq_sub_zone: null },
    "bethel census area": {
      wind_tier: null,
      eq_zone: "AK",
      eq_sub_zone: null,
    },
    "bristol bay": { wind_tier: null, eq_zone: "AK", eq_sub_zone: null },
    "bristol bay borough": {
      wind_tier: null,
      eq_zone: "AK",
      eq_sub_zone: null,
    },
    chugach: { wind_tier: null, eq_zone: "AK", eq_sub_zone: null },
    "chugach census area": {
      wind_tier: null,
      eq_zone: "AK",
      eq_sub_zone: null,
    },
    "copper river": { wind_tier: null, eq_zone: "AK", eq_sub_zone: null },
    "copper river census area": {
      wind_tier: null,
      eq_zone: "AK",
      eq_sub_zone: null,
    },
    denali: { wind_tier: null, eq_zone: "AK", eq_sub_zone: null },
    "denali borough": {
      wind_tier: null,
      eq_zone: "AK",
      eq_sub_zone: null,
    },
    dillingham: { wind_tier: null, eq_zone: "AK", eq_sub_zone: null },
    "dillingham census area": {
      wind_tier: null,
      eq_zone: "AK",
      eq_sub_zone: null,
    },
    "fairbanks north star": {
      wind_tier: null,
      eq_zone: "AK",
      eq_sub_zone: null,
    },
    "fairbanks north star borough": {
      wind_tier: null,
      eq_zone: "AK",
      eq_sub_zone: null,
    },
    haines: { wind_tier: null, eq_zone: "AK", eq_sub_zone: null },
    "haines borough": {
      wind_tier: null,
      eq_zone: "AK",
      eq_sub_zone: null,
    },
    "hoonah-angoon": {
      wind_tier: null,
      eq_zone: "AK",
      eq_sub_zone: null,
    },
    "hoonah-angoon census area": {
      wind_tier: null,
      eq_zone: "AK",
      eq_sub_zone: null,
    },
    juneau: { wind_tier: null, eq_zone: "AK", eq_sub_zone: null },
    "juneau city and borough": {
      wind_tier: null,
      eq_zone: "AK",
      eq_sub_zone: null,
    },
    "kenai peninsula": {
      wind_tier: null,
      eq_zone: "AK",
      eq_sub_zone: null,
    },
    "kenai peninsula borough": {
      wind_tier: null,
      eq_zone: "AK",
      eq_sub_zone: null,
    },
    "ketchikan gateway": {
      wind_tier: null,
      eq_zone: "AK",
      eq_sub_zone: null,
    },
    "ketchikan gateway borough": {
      wind_tier: null,
      eq_zone: "AK",
      eq_sub_zone: null,
    },
    "kodiak island": {
      wind_tier: null,
      eq_zone: "AK",
      eq_sub_zone: null,
    },
    "kodiak island borough": {
      wind_tier: null,
      eq_zone: "AK",
      eq_sub_zone: null,
    },
    kusilvak: { wind_tier: null, eq_zone: "AK", eq_sub_zone: null },
    "kusilvak census area": {
      wind_tier: null,
      eq_zone: "AK",
      eq_sub_zone: null,
    },
    "lake and peninsula": {
      wind_tier: null,
      eq_zone: "AK",
      eq_sub_zone: null,
    },
    "lake and peninsula borough": {
      wind_tier: null,
      eq_zone: "AK",
      eq_sub_zone: null,
    },
    "matanuska-susitna": {
      wind_tier: null,
      eq_zone: "AK",
      eq_sub_zone: null,
    },
    "matanuska-susitna borough": {
      wind_tier: null,
      eq_zone: "AK",
      eq_sub_zone: null,
    },
    nome: { wind_tier: null, eq_zone: "AK", eq_sub_zone: null },
    "nome census area": {
      wind_tier: null,
      eq_zone: "AK",
      eq_sub_zone: null,
    },
    "north slope": { wind_tier: null, eq_zone: "AK", eq_sub_zone: null },
    "north slope borough": {
      wind_tier: null,
      eq_zone: "AK",
      eq_sub_zone: null,
    },
    "northwest arctic": {
      wind_tier: null,
      eq_zone: "AK",
      eq_sub_zone: null,
    },
    "northwest arctic borough": {
      wind_tier: null,
      eq_zone: "AK",
      eq_sub_zone: null,
    },
    petersburg: { wind_tier: null, eq_zone: "AK", eq_sub_zone: null },
    "petersburg borough": {
      wind_tier: null,
      eq_zone: "AK",
      eq_sub_zone: null,
    },
    "prince of wales-hyder": {
      wind_tier: null,
      eq_zone: "AK",
      eq_sub_zone: null,
    },
    "prince of wales-hyder census area": {
      wind_tier: null,
      eq_zone: "AK",
      eq_sub_zone: null,
    },
    sitka: { wind_tier: null, eq_zone: "AK", eq_sub_zone: null },
    "sitka city and borough": {
      wind_tier: null,
      eq_zone: "AK",
      eq_sub_zone: null,
    },
    skagway: { wind_tier: null, eq_zone: "AK", eq_sub_zone: null },
    "skagway municipality": {
      wind_tier: null,
      eq_zone: "AK",
      eq_sub_zone: null,
    },
    "southeast fairbanks": {
      wind_tier: null,
      eq_zone: "AK",
      eq_sub_zone: null,
    },
    "southeast fairbanks census area": {
      wind_tier: null,
      eq_zone: "AK",
      eq_sub_zone: null,
    },
    wrangell: { wind_tier: null, eq_zone: "AK", eq_sub_zone: null },
    "wrangell city and borough": {
      wind_tier: null,
      eq_zone: "AK",
      eq_sub_zone: null,
    },
    yakutat: { wind_tier: null, eq_zone: "AK", eq_sub_zone: null },
    "yakutat city and borough": {
      wind_tier: null,
      eq_zone: "AK",
      eq_sub_zone: null,
    },
    "yukon-koyukuk": {
      wind_tier: null,
      eq_zone: "AK",
      eq_sub_zone: null,
    },
    "yukon-koyukuk census area": {
      wind_tier: null,
      eq_zone: "AK",
      eq_sub_zone: null,
    },
  },
  al: {
    baldwin: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "baldwin county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    clarke: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "clarke county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    covington: {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "covington county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    escambia: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "escambia county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    geneva: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "geneva county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    houston: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "houston county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    mobile: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "mobile county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    monroe: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "monroe county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    washington: {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "washington county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
  },
  ar: {
    arkansas: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "arkansas county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    ashley: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "ashley county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    bradley: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "bradley county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    chicot: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "chicot county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    clay: { wind_tier: null, eq_zone: "New Madrid", eq_sub_zone: null },
    "clay county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    cleburne: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "cleburne county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    cleveland: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "cleveland county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    craighead: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "craighead county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    crittenden: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "crittenden county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    cross: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "cross county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    desha: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "desha county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    drew: { wind_tier: null, eq_zone: "New Madrid", eq_sub_zone: null },
    "drew county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    faulkner: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "faulkner county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    fulton: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "fulton county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    greene: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "greene county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    independence: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "independence county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    izard: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "izard county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    jackson: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "jackson county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    jefferson: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "jefferson county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    lawrence: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "lawrence county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    lee: { wind_tier: null, eq_zone: "New Madrid", eq_sub_zone: null },
    "lee county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    lincoln: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "lincoln county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    lonoke: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "lonoke county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    mississippi: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "mississippi county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    monroe: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "monroe county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    phillips: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "phillips county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    poinsett: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "poinsett county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    prairie: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "prairie county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    pulaski: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "pulaski county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    randolph: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "randolph county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    sharp: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "sharp county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "st francis": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "st francis county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    stone: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "stone county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    white: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "white county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    woodruff: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "woodruff county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
  },
  as: {
    eastern: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "eastern district": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    western: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "western district": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
  },
  ca: {
    alameda: { wind_tier: null, eq_zone: "CA", eq_sub_zone: "A2" },
    "alameda county": {
      wind_tier: null,
      eq_zone: "CA",
      eq_sub_zone: "A2",
    },
    alpine: { wind_tier: null, eq_zone: "CA", eq_sub_zone: "E" },
    "alpine county": { wind_tier: null, eq_zone: "CA", eq_sub_zone: "E" },
    amador: { wind_tier: null, eq_zone: "CA", eq_sub_zone: "G" },
    "amador county": { wind_tier: null, eq_zone: "CA", eq_sub_zone: "G" },
    butte: { wind_tier: null, eq_zone: "CA", eq_sub_zone: "G" },
    "butte county": { wind_tier: null, eq_zone: "CA", eq_sub_zone: "G" },
    calaveras: { wind_tier: null, eq_zone: "CA", eq_sub_zone: "G" },
    "calaveras county": {
      wind_tier: null,
      eq_zone: "CA",
      eq_sub_zone: "G",
    },
    colusa: { wind_tier: null, eq_zone: "CA", eq_sub_zone: "G" },
    "colusa county": { wind_tier: null, eq_zone: "CA", eq_sub_zone: "G" },
    "contra costa": { wind_tier: null, eq_zone: "CA", eq_sub_zone: "A2" },
    "contra costa county": {
      wind_tier: null,
      eq_zone: "CA",
      eq_sub_zone: "A2",
    },
    "del norte": { wind_tier: null, eq_zone: "CA", eq_sub_zone: "A3" },
    "del norte county": {
      wind_tier: null,
      eq_zone: "CA",
      eq_sub_zone: "A3",
    },
    "el dorado": { wind_tier: null, eq_zone: "CA", eq_sub_zone: "G" },
    "el dorado county": {
      wind_tier: null,
      eq_zone: "CA",
      eq_sub_zone: "G",
    },
    fresno: { wind_tier: null, eq_zone: "CA", eq_sub_zone: "F" },
    "fresno county": { wind_tier: null, eq_zone: "CA", eq_sub_zone: "F" },
    glenn: { wind_tier: null, eq_zone: "CA", eq_sub_zone: "G" },
    "glenn county": { wind_tier: null, eq_zone: "CA", eq_sub_zone: "G" },
    humboldt: { wind_tier: null, eq_zone: "CA", eq_sub_zone: "A3" },
    "humboldt county": {
      wind_tier: null,
      eq_zone: "CA",
      eq_sub_zone: "A3",
    },
    imperial: { wind_tier: null, eq_zone: "CA", eq_sub_zone: "E" },
    "imperial county": {
      wind_tier: null,
      eq_zone: "CA",
      eq_sub_zone: "E",
    },
    inyo: { wind_tier: null, eq_zone: "CA", eq_sub_zone: "E" },
    "inyo county": { wind_tier: null, eq_zone: "CA", eq_sub_zone: "E" },
    kern: { wind_tier: null, eq_zone: "CA", eq_sub_zone: "C" },
    "kern county": { wind_tier: null, eq_zone: "CA", eq_sub_zone: "C" },
    kings: { wind_tier: null, eq_zone: "CA", eq_sub_zone: "F" },
    "kings county": { wind_tier: null, eq_zone: "CA", eq_sub_zone: "F" },
    lake: { wind_tier: null, eq_zone: "CA", eq_sub_zone: "A3" },
    "lake county": { wind_tier: null, eq_zone: "CA", eq_sub_zone: "A3" },
    lassen: { wind_tier: null, eq_zone: "CA", eq_sub_zone: "H" },
    "lassen county": { wind_tier: null, eq_zone: "CA", eq_sub_zone: "H" },
    "los angeles": { wind_tier: null, eq_zone: "CA", eq_sub_zone: "B1" },
    "los angeles county": {
      wind_tier: null,
      eq_zone: "CA",
      eq_sub_zone: "B1",
    },
    madera: { wind_tier: null, eq_zone: "CA", eq_sub_zone: "F" },
    "madera county": { wind_tier: null, eq_zone: "CA", eq_sub_zone: "F" },
    marin: { wind_tier: null, eq_zone: "CA", eq_sub_zone: "A3" },
    "marin county": { wind_tier: null, eq_zone: "CA", eq_sub_zone: "A3" },
    mariposa: { wind_tier: null, eq_zone: "CA", eq_sub_zone: "F" },
    "mariposa county": {
      wind_tier: null,
      eq_zone: "CA",
      eq_sub_zone: "F",
    },
    mendocino: { wind_tier: null, eq_zone: "CA", eq_sub_zone: "A3" },
    "mendocino county": {
      wind_tier: null,
      eq_zone: "CA",
      eq_sub_zone: "A3",
    },
    merced: { wind_tier: null, eq_zone: "CA", eq_sub_zone: "F" },
    "merced county": { wind_tier: null, eq_zone: "CA", eq_sub_zone: "F" },
    modoc: { wind_tier: null, eq_zone: "CA", eq_sub_zone: "H" },
    "modoc county": { wind_tier: null, eq_zone: "CA", eq_sub_zone: "H" },
    mono: { wind_tier: null, eq_zone: "CA", eq_sub_zone: "E" },
    "mono county": { wind_tier: null, eq_zone: "CA", eq_sub_zone: "E" },
    monterey: { wind_tier: null, eq_zone: "CA", eq_sub_zone: "A3" },
    "monterey county": {
      wind_tier: null,
      eq_zone: "CA",
      eq_sub_zone: "A3",
    },
    napa: { wind_tier: null, eq_zone: "CA", eq_sub_zone: "A3" },
    "napa county": { wind_tier: null, eq_zone: "CA", eq_sub_zone: "A3" },
    nevada: { wind_tier: null, eq_zone: "CA", eq_sub_zone: "G" },
    "nevada county": { wind_tier: null, eq_zone: "CA", eq_sub_zone: "G" },
    orange: { wind_tier: null, eq_zone: "CA", eq_sub_zone: "B3" },
    "orange county": {
      wind_tier: null,
      eq_zone: "CA",
      eq_sub_zone: "B3",
    },
    placer: { wind_tier: null, eq_zone: "CA", eq_sub_zone: "G" },
    "placer county": { wind_tier: null, eq_zone: "CA", eq_sub_zone: "G" },
    plumas: { wind_tier: null, eq_zone: "CA", eq_sub_zone: "H" },
    "plumas county": { wind_tier: null, eq_zone: "CA", eq_sub_zone: "H" },
    riverside: { wind_tier: null, eq_zone: "CA", eq_sub_zone: "E" },
    "riverside county": {
      wind_tier: null,
      eq_zone: "CA",
      eq_sub_zone: "E",
    },
    sacramento: { wind_tier: null, eq_zone: "CA", eq_sub_zone: "G" },
    "sacramento county": {
      wind_tier: null,
      eq_zone: "CA",
      eq_sub_zone: "G",
    },
    "san benito": { wind_tier: null, eq_zone: "CA", eq_sub_zone: "A3" },
    "san benito county": {
      wind_tier: null,
      eq_zone: "CA",
      eq_sub_zone: "A3",
    },
    "san bernardino": {
      wind_tier: null,
      eq_zone: "CA",
      eq_sub_zone: "E",
    },
    "san bernardino county": {
      wind_tier: null,
      eq_zone: "CA",
      eq_sub_zone: "E",
    },
    "san diego": { wind_tier: null, eq_zone: "CA", eq_sub_zone: "D" },
    "san diego county": {
      wind_tier: null,
      eq_zone: "CA",
      eq_sub_zone: "D",
    },
    "san francisco": {
      wind_tier: null,
      eq_zone: "CA",
      eq_sub_zone: "A1",
    },
    "san francisco county": {
      wind_tier: null,
      eq_zone: "CA",
      eq_sub_zone: "A1",
    },
    "san joaquin": { wind_tier: null, eq_zone: "CA", eq_sub_zone: "G" },
    "san joaquin county": {
      wind_tier: null,
      eq_zone: "CA",
      eq_sub_zone: "G",
    },
    "san luis obispo": {
      wind_tier: null,
      eq_zone: "CA",
      eq_sub_zone: "C",
    },
    "san luis obispo county": {
      wind_tier: null,
      eq_zone: "CA",
      eq_sub_zone: "C",
    },
    "san mateo": { wind_tier: null, eq_zone: "CA", eq_sub_zone: "A1" },
    "san mateo county": {
      wind_tier: null,
      eq_zone: "CA",
      eq_sub_zone: "A1",
    },
    "santa barbara": { wind_tier: null, eq_zone: "CA", eq_sub_zone: "C" },
    "santa barbara county": {
      wind_tier: null,
      eq_zone: "CA",
      eq_sub_zone: "C",
    },
    "santa clara": { wind_tier: null, eq_zone: "CA", eq_sub_zone: "A3" },
    "santa clara county": {
      wind_tier: null,
      eq_zone: "CA",
      eq_sub_zone: "A3",
    },
    "santa cruz": { wind_tier: null, eq_zone: "CA", eq_sub_zone: "A3" },
    "santa cruz county": {
      wind_tier: null,
      eq_zone: "CA",
      eq_sub_zone: "A3",
    },
    shasta: { wind_tier: null, eq_zone: "CA", eq_sub_zone: "H" },
    "shasta county": { wind_tier: null, eq_zone: "CA", eq_sub_zone: "H" },
    sierra: { wind_tier: null, eq_zone: "CA", eq_sub_zone: "H" },
    "sierra county": { wind_tier: null, eq_zone: "CA", eq_sub_zone: "H" },
    siskiyou: { wind_tier: null, eq_zone: "CA", eq_sub_zone: "H" },
    "siskiyou county": {
      wind_tier: null,
      eq_zone: "CA",
      eq_sub_zone: "H",
    },
    solano: { wind_tier: null, eq_zone: "CA", eq_sub_zone: "A3" },
    "solano county": {
      wind_tier: null,
      eq_zone: "CA",
      eq_sub_zone: "A3",
    },
    sonoma: { wind_tier: null, eq_zone: "CA", eq_sub_zone: "A3" },
    "sonoma county": {
      wind_tier: null,
      eq_zone: "CA",
      eq_sub_zone: "A3",
    },
    stanislaus: { wind_tier: null, eq_zone: "CA", eq_sub_zone: "G" },
    "stanislaus county": {
      wind_tier: null,
      eq_zone: "CA",
      eq_sub_zone: "G",
    },
    sutter: { wind_tier: null, eq_zone: "CA", eq_sub_zone: "G" },
    "sutter county": { wind_tier: null, eq_zone: "CA", eq_sub_zone: "G" },
    tehama: { wind_tier: null, eq_zone: "CA", eq_sub_zone: "H" },
    "tehama county": { wind_tier: null, eq_zone: "CA", eq_sub_zone: "H" },
    trinity: { wind_tier: null, eq_zone: "CA", eq_sub_zone: "H" },
    "trinity county": {
      wind_tier: null,
      eq_zone: "CA",
      eq_sub_zone: "H",
    },
    tulare: { wind_tier: null, eq_zone: "CA", eq_sub_zone: "F" },
    "tulare county": { wind_tier: null, eq_zone: "CA", eq_sub_zone: "F" },
    tuolumne: { wind_tier: null, eq_zone: "CA", eq_sub_zone: "G" },
    "tuolumne county": {
      wind_tier: null,
      eq_zone: "CA",
      eq_sub_zone: "G",
    },
    ventura: { wind_tier: null, eq_zone: "CA", eq_sub_zone: "C" },
    "ventura county": {
      wind_tier: null,
      eq_zone: "CA",
      eq_sub_zone: "C",
    },
    yolo: { wind_tier: null, eq_zone: "CA", eq_sub_zone: "G" },
    "yolo county": { wind_tier: null, eq_zone: "CA", eq_sub_zone: "G" },
    yuba: { wind_tier: null, eq_zone: "CA", eq_sub_zone: "G" },
    "yuba county": { wind_tier: null, eq_zone: "CA", eq_sub_zone: "G" },
  },
  ct: {
    capitol: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "capitol planning region": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "greater bridgeport": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "greater bridgeport planning region": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "lower connecticut river valley": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "lower connecticut river valley planning region": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "naugatuck valley": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "naugatuck valley planning region": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "northeastern connecticut": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "northeastern connecticut planning region": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "northwest hills": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "northwest hills planning region": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "south central connecticut": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "south central connecticut planning region": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "southeastern connecticut": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "southeastern connecticut planning region": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "western connecticut": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "western connecticut planning region": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
  },
  de: {
    kent: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "kent county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "new castle": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "new castle county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    sussex: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "sussex county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
  },
  fl: {
    alachua: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "alachua county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    baker: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "baker county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    bay: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "bay county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    bradford: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "bradford county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    brevard: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "brevard county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    broward: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "broward county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    calhoun: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "calhoun county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    charlotte: {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "charlotte county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    citrus: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "citrus county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    clay: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "clay county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    collier: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "collier county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    columbia: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "columbia county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    desoto: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "desoto county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    dixie: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "dixie county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    duval: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "duval county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    escambia: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "escambia county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    flagler: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "flagler county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    franklin: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "franklin county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    gadsden: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "gadsden county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    gilchrist: {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "gilchrist county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    glades: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "glades county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    gulf: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "gulf county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    hamilton: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "hamilton county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    hardee: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "hardee county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    hendry: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "hendry county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    hernando: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "hernando county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    highlands: {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "highlands county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    hillsborough: {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "hillsborough county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    holmes: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "holmes county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "indian river": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "indian river county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    jackson: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "jackson county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    jefferson: {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "jefferson county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    lafayette: {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "lafayette county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    lake: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "lake county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    lee: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "lee county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    leon: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "leon county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    levy: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "levy county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    liberty: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "liberty county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    madison: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "madison county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    manatee: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "manatee county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    marion: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "marion county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    martin: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "martin county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "miami-dade": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "miami-dade county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    monroe: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "monroe county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    nassau: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "nassau county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    okaloosa: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "okaloosa county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    okeechobee: {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "okeechobee county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    orange: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "orange county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    osceola: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "osceola county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "palm beach": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "palm beach county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    pasco: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "pasco county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    pinellas: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "pinellas county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    polk: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "polk county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    putnam: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "putnam county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "santa rosa": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "santa rosa county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    sarasota: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "sarasota county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    seminole: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "seminole county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "st johns": { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "st johns county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "st lucie": { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "st lucie county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    sumter: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "sumter county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    suwannee: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "suwannee county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    taylor: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "taylor county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    union: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "union county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    volusia: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "volusia county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    wakulla: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "wakulla county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    walton: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "walton county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    washington: {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "washington county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    _default: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
  },
  ga: {
    brantley: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "brantley county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    brooks: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "brooks county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    bryan: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "bryan county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    bulloch: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "bulloch county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    camden: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "camden county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    charlton: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "charlton county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    chatham: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "chatham county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    clinch: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "clinch county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    decatur: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "decatur county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    echols: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "echols county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    effingham: {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "effingham county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    evans: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "evans county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    glynn: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "glynn county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    grady: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "grady county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    liberty: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "liberty county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    long: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "long county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    lowndes: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "lowndes county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    mcintosh: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "mcintosh county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    seminole: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "seminole county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    tattnall: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "tattnall county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    thomas: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "thomas county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    ware: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "ware county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    wayne: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "wayne county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
  },
  hi: {
    hawaii: { wind_tier: null, eq_zone: "HI", eq_sub_zone: null },
    "hawaii county": {
      wind_tier: null,
      eq_zone: "HI",
      eq_sub_zone: null,
    },
    honolulu: { wind_tier: null, eq_zone: "HI", eq_sub_zone: null },
    "honolulu county": {
      wind_tier: null,
      eq_zone: "HI",
      eq_sub_zone: null,
    },
    kalawao: { wind_tier: null, eq_zone: "HI", eq_sub_zone: null },
    "kalawao county": {
      wind_tier: null,
      eq_zone: "HI",
      eq_sub_zone: null,
    },
    kauai: { wind_tier: null, eq_zone: "HI", eq_sub_zone: null },
    "kauai county": { wind_tier: null, eq_zone: "HI", eq_sub_zone: null },
    maui: { wind_tier: null, eq_zone: "HI", eq_sub_zone: null },
    "maui county": { wind_tier: null, eq_zone: "HI", eq_sub_zone: null },
    _default: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
  },
  il: {
    alexander: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "alexander county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    bond: { wind_tier: null, eq_zone: "New Madrid", eq_sub_zone: null },
    "bond county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    clay: { wind_tier: null, eq_zone: "New Madrid", eq_sub_zone: null },
    "clay county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    clinton: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "clinton county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    edwards: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "edwards county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    franklin: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "franklin county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    gallatin: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "gallatin county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    hamilton: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "hamilton county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    hardin: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "hardin county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    jackson: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "jackson county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    jefferson: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "jefferson county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    johnson: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "johnson county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    lawrence: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "lawrence county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    madison: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "madison county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    marion: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "marion county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    massac: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "massac county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    monroe: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "monroe county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    perry: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "perry county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    pope: { wind_tier: null, eq_zone: "New Madrid", eq_sub_zone: null },
    "pope county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    pulaski: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "pulaski county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    randolph: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "randolph county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    richland: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "richland county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    saline: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "saline county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "st clair": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "st clair county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    union: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "union county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    wabash: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "wabash county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    washington: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "washington county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    wayne: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "wayne county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    white: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "white county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    williamson: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "williamson county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
  },
  in: {
    crawford: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "crawford county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    daviess: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "daviess county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    dubois: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "dubois county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    gibson: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "gibson county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    greene: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "greene county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    knox: { wind_tier: null, eq_zone: "New Madrid", eq_sub_zone: null },
    "knox county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    lawrence: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "lawrence county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    martin: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "martin county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    orange: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "orange county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    perry: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "perry county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    pike: { wind_tier: null, eq_zone: "New Madrid", eq_sub_zone: null },
    "pike county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    posey: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "posey county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    spencer: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "spencer county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    sullivan: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "sullivan county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    vanderburgh: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "vanderburgh county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    warrick: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "warrick county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
  },
  ky: {
    ballard: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "ballard county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    breckinridge: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "breckinridge county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    butler: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "butler county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    caldwell: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "caldwell county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    calloway: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "calloway county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    carlisle: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "carlisle county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    christian: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "christian county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    crittenden: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "crittenden county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    daviess: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "daviess county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    fulton: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "fulton county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    graves: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "graves county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    hancock: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "hancock county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    henderson: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "henderson county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    hickman: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "hickman county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    hopkins: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "hopkins county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    livingston: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "livingston county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    logan: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "logan county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    lyon: { wind_tier: null, eq_zone: "New Madrid", eq_sub_zone: null },
    "lyon county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    marshall: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "marshall county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    mccracken: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "mccracken county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    mclean: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "mclean county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    muhlenberg: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "muhlenberg county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    ohio: { wind_tier: null, eq_zone: "New Madrid", eq_sub_zone: null },
    "ohio county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    simpson: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "simpson county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    todd: { wind_tier: null, eq_zone: "New Madrid", eq_sub_zone: null },
    "todd county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    trigg: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "trigg county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    union: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "union county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    warren: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "warren county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    webster: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "webster county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
  },
  la: {
    acadia: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "acadia parish": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    assumption: {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "assumption parish": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    calcasieu: {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "calcasieu parish": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    cameron: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "cameron parish": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    iberia: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "iberia parish": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    iberville: {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "iberville parish": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    jefferson: {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "jefferson parish": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "jefferson davis": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "jefferson davis parish": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    lafayette: {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "lafayette parish": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    lafourche: {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "lafourche parish": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    orleans: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "orleans parish": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    plaquemines: {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "plaquemines parish": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "st bernard": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "st bernard parish": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "st charles": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "st charles parish": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "st james": { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "st james parish": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "st john the baptist": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "st john the baptist parish": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "st martin": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "st martin parish": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "st mary": { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "st mary parish": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "st tammany": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "st tammany parish": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    tangipahoa: {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "tangipahoa parish": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    terrebonne: {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "terrebonne parish": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    vermilion: {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "vermilion parish": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    washington: {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "washington parish": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
  },
  ma: {
    barnstable: {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "barnstable county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    bristol: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "bristol county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    dukes: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "dukes county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    essex: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "essex county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    middlesex: {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "middlesex county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    nantucket: {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "nantucket county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    norfolk: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "norfolk county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    plymouth: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "plymouth county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    suffolk: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "suffolk county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    worcester: {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "worcester county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
  },
  md: {
    "anne arundel": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "anne arundel county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    baltimore: {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "baltimore city": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "baltimore county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    calvert: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "calvert county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    caroline: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "caroline county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    carroll: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "carroll county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    cecil: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "cecil county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    charles: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "charles county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    dorchester: {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "dorchester county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    harford: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "harford county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    howard: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "howard county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    kent: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "kent county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    montgomery: { wind_tier: null, eq_zone: null, eq_sub_zone: null },
    "montgomery county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "prince george's": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "prince george's county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "queen anne's": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "queen anne's county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    somerset: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "somerset county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "st mary's": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "st mary's county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    talbot: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "talbot county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    wicomico: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "wicomico county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    worcester: {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "worcester county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
  },
  me: {
    androscoggin: {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "androscoggin county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    aroostook: {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "aroostook county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    cumberland: {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "cumberland county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    hancock: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "hancock county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    kennebec: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "kennebec county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    knox: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "knox county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    lincoln: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "lincoln county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    oxford: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "oxford county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    penobscot: {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "penobscot county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    sagadahoc: {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "sagadahoc county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    somerset: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "somerset county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    waldo: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "waldo county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    washington: {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "washington county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    york: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "york county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
  },
  mo: {
    bollinger: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "bollinger county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    butler: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "butler county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "cape girardeau": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "cape girardeau county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    carter: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "carter county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    dunklin: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "dunklin county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    iron: { wind_tier: null, eq_zone: "New Madrid", eq_sub_zone: null },
    "iron county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    jefferson: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "jefferson county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    madison: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "madison county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    mississippi: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "mississippi county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "new madrid": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "new madrid county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    oregon: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "oregon county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    pemiscot: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "pemiscot county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    perry: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "perry county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    reynolds: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "reynolds county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    ripley: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "ripley county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    scott: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "scott county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "st charles": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "st charles county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "st francois": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "st francois county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "st louis": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "st louis county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "st louis city": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "ste genevieve": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "ste genevieve county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    stoddard: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "stoddard county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    washington: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "washington county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    wayne: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "wayne county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
  },
  ms: {
    alcorn: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "alcorn county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    benton: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "benton county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    bolivar: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "bolivar county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    calhoun: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "calhoun county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    carroll: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "carroll county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    chickasaw: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "chickasaw county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    coahoma: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "coahoma county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    desoto: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "desoto county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    george: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "george county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    greene: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "greene county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    grenada: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "grenada county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    hancock: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "hancock county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    harrison: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "harrison county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    holmes: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "holmes county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    humphreys: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "humphreys county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    issaquena: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "issaquena county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    jackson: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "jackson county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    lafayette: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "lafayette county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    leflore: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "leflore county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    marshall: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "marshall county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    montgomery: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "montgomery county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    panola: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "panola county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "pearl river": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "pearl river county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    pontotoc: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "pontotoc county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    quitman: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "quitman county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    sharkey: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "sharkey county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    stone: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "stone county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    sunflower: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "sunflower county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    tallahatchie: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "tallahatchie county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    tate: { wind_tier: null, eq_zone: "New Madrid", eq_sub_zone: null },
    "tate county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    tippah: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "tippah county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    tunica: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "tunica county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    union: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "union county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    warren: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "warren county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    washington: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "washington county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    webster: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "webster county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    yalobusha: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "yalobusha county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    yazoo: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "yazoo county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
  },
  nc: {
    beaufort: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "beaufort county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    bladen: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "bladen county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    brunswick: {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "brunswick county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    camden: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "camden county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    carteret: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "carteret county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    columbus: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "columbus county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    craven: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "craven county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    currituck: {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "currituck county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    dare: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "dare county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    duplin: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "duplin county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    hyde: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "hyde county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    jones: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "jones county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "new hanover": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "new hanover county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    onslow: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "onslow county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    pamlico: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "pamlico county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    pender: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "pender county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    robeson: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "robeson county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    sampson: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "sampson county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    tyrrell: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "tyrrell county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    washington: {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "washington county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
  },
  nh: {
    carroll: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "carroll county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    hillsborough: {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "hillsborough county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    merrimack: {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "merrimack county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    rockingham: {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "rockingham county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    strafford: {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "strafford county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
  },
  nj: {
    atlantic: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "atlantic county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    bergen: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "bergen county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    burlington: {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "burlington county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    camden: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "camden county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "cape may": { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "cape may county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    cumberland: {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "cumberland county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    gloucester: {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "gloucester county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    hudson: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "hudson county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    mercer: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "mercer county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    middlesex: {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "middlesex county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    monmouth: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "monmouth county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    ocean: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "ocean county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    salem: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "salem county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    somerset: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "somerset county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    union: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "union county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
  },
  ny: {
    bronx: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "bronx county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    dutchess: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "dutchess county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    kings: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "kings county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    nassau: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "nassau county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "new york": { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "new york county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    putnam: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "putnam county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    queens: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "queens county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    richmond: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "richmond county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    rockland: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "rockland county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    suffolk: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "suffolk county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    westchester: {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "westchester county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
  },
  or: {
    baker: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "baker county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    benton: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "benton county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    clackamas: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "clackamas county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    clatsop: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "clatsop county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    columbia: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "columbia county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    coos: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "coos county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    crook: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "crook county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    curry: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "curry county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    deschutes: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "deschutes county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    douglas: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "douglas county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    gilliam: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "gilliam county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    grant: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "grant county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    harney: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "harney county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    "hood river": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    "hood river county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    jackson: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "jackson county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    jefferson: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "jefferson county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    josephine: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "josephine county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    klamath: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "klamath county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    lake: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "lake county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    lane: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "lane county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    lincoln: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "lincoln county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    linn: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "linn county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    malheur: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "malheur county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    marion: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "marion county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    morrow: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "morrow county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    multnomah: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "multnomah county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    polk: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "polk county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    sherman: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "sherman county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    tillamook: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "tillamook county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    umatilla: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "umatilla county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    union: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "union county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    wallowa: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "wallowa county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    wasco: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "wasco county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    washington: {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    "washington county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    wheeler: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "wheeler county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    yamhill: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "yamhill county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
  },
  pa: {
    chester: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "chester county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    delaware: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "delaware county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    lancaster: {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "lancaster county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    york: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "york county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
  },
  pr: {
    adjuntas: { wind_tier: null, eq_zone: "PR", eq_sub_zone: null },
    "adjuntas municipio": {
      wind_tier: null,
      eq_zone: "PR",
      eq_sub_zone: null,
    },
    aguadilla: { wind_tier: null, eq_zone: "PR", eq_sub_zone: null },
    "aguadilla municipio": {
      wind_tier: null,
      eq_zone: "PR",
      eq_sub_zone: null,
    },
    aibonito: { wind_tier: null, eq_zone: "PR", eq_sub_zone: null },
    "aibonito municipio": {
      wind_tier: null,
      eq_zone: "PR",
      eq_sub_zone: null,
    },
    arecibo: { wind_tier: null, eq_zone: "PR", eq_sub_zone: null },
    "arecibo municipio": {
      wind_tier: null,
      eq_zone: "PR",
      eq_sub_zone: null,
    },
    barceloneta: { wind_tier: null, eq_zone: "PR", eq_sub_zone: null },
    "barceloneta municipio": {
      wind_tier: null,
      eq_zone: "PR",
      eq_sub_zone: null,
    },
    "bayam\u00f3n": { wind_tier: null, eq_zone: "PR", eq_sub_zone: null },
    "bayam\u00f3n municipio": {
      wind_tier: null,
      eq_zone: "PR",
      eq_sub_zone: null,
    },
    "can\u00f3vanas": {
      wind_tier: null,
      eq_zone: "PR",
      eq_sub_zone: null,
    },
    "can\u00f3vanas municipio": {
      wind_tier: null,
      eq_zone: "PR",
      eq_sub_zone: null,
    },
    carolina: { wind_tier: null, eq_zone: "PR", eq_sub_zone: null },
    "carolina municipio": {
      wind_tier: null,
      eq_zone: "PR",
      eq_sub_zone: null,
    },
    "cata\u00f1o": { wind_tier: null, eq_zone: "PR", eq_sub_zone: null },
    "cata\u00f1o municipio": {
      wind_tier: null,
      eq_zone: "PR",
      eq_sub_zone: null,
    },
    guaynabo: { wind_tier: null, eq_zone: "PR", eq_sub_zone: null },
    "guaynabo municipio": {
      wind_tier: null,
      eq_zone: "PR",
      eq_sub_zone: null,
    },
    maricao: { wind_tier: null, eq_zone: "PR", eq_sub_zone: null },
    "maricao municipio": {
      wind_tier: null,
      eq_zone: "PR",
      eq_sub_zone: null,
    },
    patillas: { wind_tier: null, eq_zone: "PR", eq_sub_zone: null },
    "patillas municipio": {
      wind_tier: null,
      eq_zone: "PR",
      eq_sub_zone: null,
    },
    "san juan": { wind_tier: null, eq_zone: "PR", eq_sub_zone: null },
    "san juan municipio": {
      wind_tier: null,
      eq_zone: "PR",
      eq_sub_zone: null,
    },
    "santa isabel": { wind_tier: null, eq_zone: "PR", eq_sub_zone: null },
    "santa isabel municipio": {
      wind_tier: null,
      eq_zone: "PR",
      eq_sub_zone: null,
    },
    "toa alta": { wind_tier: null, eq_zone: "PR", eq_sub_zone: null },
    "toa alta municipio": {
      wind_tier: null,
      eq_zone: "PR",
      eq_sub_zone: null,
    },
    "toa baja": { wind_tier: null, eq_zone: "PR", eq_sub_zone: null },
    "toa baja municipio": {
      wind_tier: null,
      eq_zone: "PR",
      eq_sub_zone: null,
    },
    "trujillo alto": {
      wind_tier: null,
      eq_zone: "PR",
      eq_sub_zone: null,
    },
    "trujillo alto municipio": {
      wind_tier: null,
      eq_zone: "PR",
      eq_sub_zone: null,
    },
  },
  ri: {
    bristol: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "bristol county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    kent: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "kent county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    newport: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "newport county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    providence: {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "providence county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    washington: {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "washington county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
  },
  sc: {
    allendale: {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "allendale county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    bamberg: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "bamberg county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    beaufort: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "beaufort county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    berkeley: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "berkeley county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    charleston: {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "charleston county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    colleton: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "colleton county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    dillon: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "dillon county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    dorchester: {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "dorchester county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    georgetown: {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "georgetown county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    hampton: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "hampton county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    horry: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "horry county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    jasper: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "jasper county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    marion: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "marion county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    orangeburg: {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "orangeburg county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    williamsburg: {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "williamsburg county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
  },
  tn: {
    benton: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "benton county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    carroll: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "carroll county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    chester: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "chester county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    crockett: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "crockett county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    decatur: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "decatur county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    dyer: { wind_tier: null, eq_zone: "New Madrid", eq_sub_zone: null },
    "dyer county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    fayette: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "fayette county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    gibson: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "gibson county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    hardeman: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "hardeman county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    hardin: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "hardin county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    haywood: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "haywood county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    henderson: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "henderson county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    henry: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "henry county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    humphreys: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "humphreys county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    lake: { wind_tier: null, eq_zone: "New Madrid", eq_sub_zone: null },
    "lake county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    lauderdale: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "lauderdale county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    madison: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "madison county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    mcnairy: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "mcnairy county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    obion: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "obion county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    shelby: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "shelby county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    stewart: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "stewart county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    tipton: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "tipton county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    weakley: {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
    "weakley county": {
      wind_tier: null,
      eq_zone: "New Madrid",
      eq_sub_zone: null,
    },
  },
  tx: {
    aransas: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "aransas county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    brazoria: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "brazoria county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    brooks: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "brooks county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    calhoun: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "calhoun county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    cameron: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "cameron county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    chambers: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "chambers county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "fort bend": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "fort bend county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    galveston: {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "galveston county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    hardin: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "hardin county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    harris: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "harris county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    hidalgo: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "hidalgo county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    jackson: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "jackson county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    jefferson: {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "jefferson county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "jim wells": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "jim wells county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    kenedy: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "kenedy county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    kleberg: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "kleberg county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    liberty: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "liberty county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    matagorda: {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "matagorda county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    montgomery: {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "montgomery county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    nueces: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "nueces county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    orange: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "orange county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    refugio: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "refugio county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "san patricio": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "san patricio county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    victoria: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "victoria county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    waller: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "waller county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    wharton: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "wharton county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    willacy: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "willacy county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
  },
  va: {
    accomack: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "accomack county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    alexandria: {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "alexandria city": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    arlington: {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "arlington county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    caroline: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "caroline county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "charles city": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "charles city county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    chesapeake: {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "chesapeake city": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    culpeper: { wind_tier: null, eq_zone: null, eq_sub_zone: null },
    "culpeper county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    essex: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "essex county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    fairfax: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "fairfax city": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "fairfax county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "falls church": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "falls church city": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    fauquier: { wind_tier: null, eq_zone: null, eq_sub_zone: null },
    "fauquier county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    fredericksburg: {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "fredericksburg city": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    gloucester: {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "gloucester county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    hampton: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "hampton city": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "isle of wight": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "isle of wight county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "james city": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "james city county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "king and queen": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "king and queen county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "king george": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "king george county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    lancaster: {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "lancaster county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    loudoun: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "loudoun county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    manassas: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "manassas city": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "manassas park": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "manassas park city": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    mathews: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "mathews county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    middlesex: {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "middlesex county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "new kent": { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "new kent county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "newport news": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "newport news city": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    norfolk: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "norfolk city": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    northampton: {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "northampton county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    northumberland: {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "northumberland county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    poquoson: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "poquoson city": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    portsmouth: {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "portsmouth city": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "prince william": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "prince william county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    richmond: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "richmond county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    spotsylvania: { wind_tier: null, eq_zone: null, eq_sub_zone: null },
    "spotsylvania county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    stafford: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "stafford county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    suffolk: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "suffolk city": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    surry: { wind_tier: "Tier 2", eq_zone: null, eq_sub_zone: null },
    "surry county": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "virginia beach": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "virginia beach city": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    westmoreland: {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "westmoreland county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    williamsburg: {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    "williamsburg city": {
      wind_tier: "Tier 2",
      eq_zone: null,
      eq_sub_zone: null,
    },
    york: { wind_tier: "Tier 1", eq_zone: null, eq_sub_zone: null },
    "york county": {
      wind_tier: "Tier 1",
      eq_zone: null,
      eq_sub_zone: null,
    },
    loudon: { wind_tier: null, eq_zone: null, eq_sub_zone: null },
  },
  wa: {
    adams: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "adams county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    asotin: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "asotin county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    benton: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "benton county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    chelan: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "chelan county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    clallam: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "clallam county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    clark: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "clark county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    columbia: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "columbia county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    cowlitz: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "cowlitz county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    douglas: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "douglas county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    ferry: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "ferry county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    franklin: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "franklin county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    garfield: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "garfield county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    grant: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "grant county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    "grays harbor": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    "grays harbor county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    island: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "island county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    jefferson: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "jefferson county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    king: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "king county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    kitsap: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "kitsap county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    kittitas: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "kittitas county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    klickitat: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "klickitat county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    lewis: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "lewis county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    lincoln: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "lincoln county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    mason: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "mason county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    okanogan: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "okanogan county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    pacific: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "pacific county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    "pend oreille": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    "pend oreille county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    pierce: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "pierce county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    "san juan": { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "san juan county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    skagit: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "skagit county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    skamania: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "skamania county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    snohomish: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "snohomish county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    spokane: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "spokane county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    stevens: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "stevens county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    thurston: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "thurston county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    wahkiakum: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "wahkiakum county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    "walla walla": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    "walla walla county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    whatcom: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "whatcom county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    whitman: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "whitman county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
    yakima: { wind_tier: null, eq_zone: "PacNW", eq_sub_zone: null },
    "yakima county": {
      wind_tier: null,
      eq_zone: "PacNW",
      eq_sub_zone: null,
    },
  },
};

export function getWindTierAndEqData(
  county: string | null | undefined,
  state: string | null | undefined,
): {
  wind_tier?: string | null;
  eq_zone?: string | null;
  eq_sub_zone?: string | null;
} {
  const cleanState = cleanLookupValue(state);
  const cleanCounty = cleanLookupValue(county);

  if (!cleanState || !cleanCounty) {
    return {
      wind_tier: null,
      eq_zone: null,
      eq_sub_zone: null,
    };
  }

  const result: {
    wind_tier?: string | null;
    eq_zone?: string | null;
    eq_sub_zone?: string | null;
  } = {
    wind_tier: null,
    eq_zone: null,
    eq_sub_zone: null,
  };

  if (cleanState in PING_LOOKUP) {
    const stateLookup = PING_LOOKUP[
      cleanState as keyof typeof PING_LOOKUP
    ] as Record<
      string,
      {
        wind_tier: string | null;
        eq_zone: string | null;
        eq_sub_zone: string | null;
      }
    >;
    if (cleanCounty in stateLookup) {
      const data = stateLookup[cleanCounty];
      if (data) {
        if (data.wind_tier !== null) result.wind_tier = data.wind_tier;
        if (data.eq_zone !== null) result.eq_zone = data.eq_zone;
        if (data.eq_sub_zone !== null) result.eq_sub_zone = data.eq_sub_zone;
      }
    } else if ("_default" in stateLookup) {
      const data = stateLookup["_default"];
      if (data) {
        if (data.wind_tier !== null) result.wind_tier = data.wind_tier;
        if (data.eq_zone !== null) result.eq_zone = data.eq_zone;
        if (data.eq_sub_zone !== null) result.eq_sub_zone = data.eq_sub_zone;
      }
    }
  }

  if (cleanState === "fl") {
    result.wind_tier = "Tier 1";
  } else if (cleanState === "hi") {
    result.wind_tier = "Tier 1";
  } else if (cleanState === "va") {
    if (cleanCounty === "prince william") {
      result.wind_tier = "Tier 2";
    } else if (cleanCounty === "fairfax") {
      result.wind_tier = "Tier 2";
    } else if (cleanCounty === "stafford") {
      result.wind_tier = "Tier 2";
    } else if (cleanCounty === "loudon") {
      result.wind_tier = null;
    } else if (cleanCounty === "fauquier") {
      result.wind_tier = null;
    } else if (cleanCounty === "culpeper") {
      result.wind_tier = null;
    } else if (cleanCounty === "spotsylvania") {
      result.wind_tier = null;
    }
  } else if (cleanState === "md" && cleanCounty === "montgomery") {
    result.wind_tier = null;
  }

  return result;
}

function cleanLookupValue(s: string | null | undefined): string | null {
  if (!s) return null;
  return s.replace(".", "").toLowerCase().trim() || null;
}

export const SECTION_LENGTH_THRESHOLDS = {
  SHORT: 20,
  LONG: 55,
};

export const ERROR_DISABLED_SECTIONS = [SOURCES.PING_HAZARD];

export const DISABLED_ENRICH_BUTTON_SOURCES = [SOURCES.PING_HAZARD];

export const INTERNATIONAL_LOCATION_ERROR =
  "**Enrichment Data Unavailable Outside of USA";

export const INTERNATIONAL_LOCATION_ERROR_SOURCES = [
  SOURCES.PING_HAZARD,
  SOURCES.INTERMAPS,
  SOURCES.HAZARDHUB,
  SOURCES.RYAN_PRECISELY_WILDFIRE,
  SOURCES.RYAN_HAZARD_V3,
  SOURCES.VEXCEL_PROPERTY_INFORMATION,
  SOURCES.VEXCEL_PROPERTY_DAMAGE,
  SOURCES.TRUE_FLOOD_RISK_FFH,
  SOURCES.PING_VALUATION,
  SOURCES.PING_PERMITS,
];

export const INITIAL_AOA_SECTIONS_OPEN = 1;

export const AOA_SEVERITY_COLORS_LOOKUP = {
  DEFAULT: "var(--ping-color-neutral-700)", // neutral, baseline
  WARNING: "var(--ping-color-warning-100)", // something’s off
  ERROR: "var(--ping-color-danger-300)", // bad, needs fixing
  CRITICAL: "var(--ping-color-danger-400)", // severe, urgent
};

export const RYAN_DATASOURCES = [
  SOURCES.RYAN_GEOCODING,
  SOURCES.RYAN_KATRISK,
  SOURCES.RYAN_HAZARD_V3,
  SOURCES.RYAN_PRECISELY_WILDFIRE,
  SOURCES.RYAN_NEARMAP,
];

export const RIGHT_PANEL_WIDTH_IN_REM = 32;

export const TEARDROP_MARKER_MINIMUM_ZOOM = 10;

// A self-serve lookup's sources; each must be in the plan's allowed_sources
// (pingutils PLAN_ENTITLEMENTS).
export const FREE_PLAN_LOOKUP_SOURCES = [
  SOURCES.PING_GEOCODING,
  SOURCES.DISTANCE_TO_COAST,
];

export const PAID_PLAN_LOOKUP_SOURCES = [
  SOURCES.PING_GEOCODING,
  SOURCES.PING_HAZARD_V2,
];

export const ORG_LOOKUP_SOURCES = [
  SOURCES.PING_GEOCODING,
  SOURCES.PING_HAZARD_V2,
];

// PH2 fields that are opt-in, comma-separated as the include_fields query param expects.
export const PH2_INCLUDE_FIELDS = "PH2.site.elevation_feet,PH2.perils.fire.protection";

export const LAYER_GOOGLE: OverlayLayer = {
  id: "GOOGLE",
  label: "Google Maps",
  name: "google_maps",
  url: [],
  layerType: "google",
};

export const LAYER_HEATMAP: OverlayLayer = {
  id: "HEATMAP",
  label: "Heat Map",
  name: "google_heat_maps",
  url: [],
  layerType: "google",
};

export const DEFAULT_MEASUREMENT_LINE_NEUTRAL = "#2d2e45";
export const DEFAULT_MEASUREMENT_LINE_BRAND = "#d92f0b";
export const DEFAULT_MEASUREMENT_FILL = "#ff6c47";

// Geocoding mode color constants
export const PING_GEOCODING_LOCATION_COLOR = "#FF6C47";
export const PING_SUGGESTED_LOCATION_COLOR = "#FFA500";
export const PING_GEOCODING_HOVERED_COLOR = "#FFFFFF";
export const PING_GEOCODING_USER_COLOR = "#00FF00";
export const PING_GEOCODING_SEARCH_COLOR = "#4A9CFE";

// Geocoding mode constants
export const USER_ANNOTATION = "USER_ANNOTATION";
export const SERVICES_TO_FILTER = ["PGPRE", "PGPOST"];

// Service description mapping for geocoding sources
export const ServiceDescription: Record<string, string> = {
  EG: "ESRI",
  GG: "Google",
  GGB: "Google - Beta",
  HH: "Hazardhub",
  HG: "Here Geocoding",
  BG: "Bing",
  LBG: "Lightbox",
  PG: "Ping",
  PSG: "Precisely",
  OrigSOV: "Orig SOV",
  TG: "Tiger",
  LBFZ: "LB FloodZone",
  T4D: "Teren4d",
  FF: "Fema Flood",
  CG: "User Verified",
  AG: "Apple",
  SL: "Sugg. Loc.",
  SM: "Smarty",
  GAV: "Google Address Validation",
  AZG: "Azure Geocoding",
  GIO: "Geocodio",
};

/**
 * Get the color for a geocoding service marker
 */
export const GET_SERVICE_COLOR = (s: string): string => {
  if (!s) {
    return "black";
  }

  if (s.includes("PG")) {
    return PING_GEOCODING_LOCATION_COLOR;
  } else if (s.includes("LBG")) {
    return "#EDF903";
  } else if (s.includes("OrigSOV")) {
    return "#bcbbc6";
  } else if (s.includes("CG")) {
    return PING_GEOCODING_USER_COLOR;
  } else if (s.includes("AZG")) {
    return "#E049FE";
  } else if (s.includes("EG")) {
    return "#E049FE";
  }

  return PING_SUGGESTED_LOCATION_COLOR;
};

/**
 * Get the display description for a geocoding source
 */
export const GET_SERVICE_DESCRIPTION = (source: {
  source: string;
  merged?: Array<{ source: string }>;
}): string => {
  if (!source?.source) return "";
  const service = source.source;

  if (source.merged && source.merged.length > 0) {
    const mergedServices = source.merged.map(
      (m) => ServiceDescription[m.source] || m.source,
    );
    return mergedServices.join(", ");
  }

  return ServiceDescription[service] || service;
};

function parseAddressComponents(address: string | undefined): {
  city: string;
  state: string;
  postal_code: string;
  country: string;
} {
  if (!address) {
    return { city: "", state: "", postal_code: "", country: "US" };
  }

  const parts = address
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

  let city = "";
  let state = "";
  let postal_code = "";
  let country = "US";

  const stateZipRegexSingle = /^([A-Z]{2})\s+([\d-]{5,})$/;
  const stateOnlyRegex = /^[A-Z]{2}$/;
  const zipOnlyRegex = /^[\d-]{5,}$/;

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    if (!part) continue;

    const combinedMatch = part.match(stateZipRegexSingle);
    if (combinedMatch && combinedMatch[1] && combinedMatch[2]) {
      state = combinedMatch[1];
      postal_code = combinedMatch[2];
      city = parts[i - 1] ?? "";
      country = parts[i + 1] ?? "US";
      break;
    }

    const nextPart = parts[i + 1];
    if (stateOnlyRegex.test(part) && nextPart && zipOnlyRegex.test(nextPart)) {
      state = part;
      postal_code = nextPart;
      city = parts[i - 1] ?? "";
      country = parts[i + 2] ?? "US";
      break;
    }
  }

  return { city, state, postal_code, country };
}

export function transformBuildingToGeocodingSources(
  itemKeyJSON: any,
): GeocoderType[] {
  const geocoders: GeocoderType[] = [];

  const externalData = itemKeyJSON.external_data;
  for (const key in externalData) {
    const data = externalData[key];
    if (!data?.latitude || !data.longitude || ["EGR"].includes(key)) {
      continue;
    }
    const addressComponents = parseAddressComponents(data.formatted_address);
    geocoders.push({
      address: data.formatted_address,
      confidence: data.confidence,
      lat: parseFloat(data.latitude),
      lng: parseFloat(data.longitude),
      precision: data.precision,
      source: key,
      consulted_datasources: data?.consulted_datasources,
      source_desc: data?.source_desc,
      ...addressComponents,
    });
  }

  const data = itemKeyJSON.data;

  if (geocoders.length === 0 && data.latitude?.value && data.longitude?.value) {
    const addressComponents = parseAddressComponents(data.full_address?.value);
    geocoders.push({
      address: data.full_address?.value,
      confidence: 100,
      lat: parseFloat(data.latitude?.value),
      lng: parseFloat(data.longitude?.value),
      precision: 100,
      source: "PG",
      consulted_datasources: null,
      source_desc: null,
      ...addressComponents,
    });
  }

  const origLatitude = parseFloat(data.latitude?.original);
  const origLongitude = parseFloat(data.longitude?.original);

  if (!isNaN(origLatitude) && !isNaN(origLongitude)) {
    const origAddress =
      data.street && data.city && data.state && data.zipcode
        ? `${data.street} ${data.city} ${data.state} ${data.zipcode}`
        : "";
    const addressComponents = parseAddressComponents(origAddress);
    const origSovData: GeocoderType = {
      address: origAddress,
      confidence: "Not Applicable",
      lat: origLatitude,
      lng: origLongitude,
      precision: "Not Applicable",
      source: "OrigSOV",
      consulted_datasources: null,
      source_desc: null,
      ...addressComponents,
    };

    geocoders.push(origSovData);
  }

  return geocoders;
}

export const LAYER_CONFIGS: { [key: string]: LayerConfig } = {
  "ping-tier-1": {
    attributes: [{ field: "NAMELSAD", label: "County", format: "DEFAULT" }],
    title: "Wind Tier",
  },
  "ping-tier-2": {
    attributes: [{ field: "NAMELSAD", label: "County", format: "DEFAULT" }],
    title: "Wind Tier",
  },
  "ping-tier-combined": {
    attributes: [{ field: "NAMELSAD", label: "County", format: "DEFAULT" }],
    title: "Wind Tier",
  },
  "ping-eq-zones": {
    attributes: [
      { field: "NAMELSAD", label: "County", format: "DEFAULT" },
      { field: "EQ_ZONE", label: "EQ Zone", format: "DEFAULT" },
      { field: "EQ_SUBZONE", label: "EQ Subzone", format: "DEFAULT" },
    ],
    title: "EQ Zone",
  },
  "ping-levees": {
    attributes: [
      { field: "SYSTEM_NAME", label: "System Name", format: "DEFAULT" },
      {
        field: "FEMA_ACCREDITATION_RATING",
        label: "FEMA Accreditation Rating",
        format: "DEFAULT",
      },
    ],
    title: "Leveed Areas",
  },
  "usgs-wbd": {
    attributes: [
      { field: "name", label: "Name", format: "DEFAULT" },
      { field: "huc12", label: "HUC12", format: "DEFAULT" },
      { field: "states", label: "State", format: "DEFAULT" },
      { field: "areaacres", label: "Area (Acres)", format: "DECIMALS_1" },
      { field: "areasqkm", label: "Area (Sq Km)", format: "DECIMALS_1" },
    ],
    title: "Watershed Boundary Dataset",
  },
  "wkfc-precisely-fireperimeter": {
    attributes: [
      { field: "FIRENAME", label: "Fire Name", format: "DEFAULT" },
      { field: "STATE", label: "State", format: "DEFAULT" },
      { field: "ACRES", label: "Acres", format: "DECIMALS_1" },
    ],
    title: "Fire Perimeter",
  },
  "nifc-wfigs-current-perimeters": {
    attributes: [
      { field: "poly_IncidentName", label: "Incident", format: "DEFAULT" },
      {
        field: "attr_UniqueFireIdentifier",
        label: "Fire ID",
        format: "DEFAULT",
      },
      { field: "attr_FireType", label: "Type", format: "DEFAULT" },
      { field: "attr_POOState", label: "State", format: "DEFAULT" },
      { field: "attr_POOCounty", label: "County", format: "DEFAULT" },
      { field: "poly_GISAcres", label: "Calculated Acres", format: "INTEGER" },
      {
        field: "attr_IncidentSize",
        label: "Incident Size (ac)",
        format: "INTEGER",
      },
      {
        field: "attr_PercentContained",
        label: "Contained",
        format: "PERCENTAGE",
      },
      { field: "attr_FireCause", label: "Cause", format: "DEFAULT" },
      {
        field: "attr_FireBehaviorGeneral",
        label: "Fire Behavior",
        format: "DEFAULT",
      },
      {
        field: "attr_EstimatedCostToDate",
        label: "Est. Cost",
        format: "CURRENCY",
      },
      {
        field: "attr_FireDiscoveryDateTime",
        label: "Discovered",
        format: "TIMESTAMP_MS",
      },
      {
        field: "poly_DateCurrent",
        label: "Last Updated",
        format: "TIMESTAMP_MS",
      },
    ],
    title: "Current Fire Perimeter",
  },
  "fire-perimeters": {
    attributes: [
      { field: "Name", label: "Fire Name", format: "DEFAULT" },
      { field: "Date", label: "Date", format: "DATE" },
      { field: "Acres", label: "Acres", format: "INTEGER" },
      { field: "Year", label: "Year", format: "DEFAULT" },
    ],
    title: "Fire Perimeter History",
  },
  "wkfc-precisely-fireriskpro": {
    attributes: [
      { field: "STATE", label: "State", format: "DEFAULT" },
      { field: "NOHARMODEL", label: "Model Type", format: "DEFAULT" },
      { field: "RISKDESC", label: "Risk Level", format: "DEFAULT" },
      { field: "BURN_PROB", label: "Burn Probability", format: "PERCENTAGE" },
      { field: "INTENSITY", label: "Fire Intensity", format: "PERCENTAGE" },
      { field: "SEVERITY", label: "Fire Severity", format: "PERCENTAGE" },
    ],
    title: "Fire Risk",
  },
  "crime-tract": {
    attributes: [
      { field: "ASSAULT", label: "Assault", format: "EASI" },
      { field: "BURGLARY", label: "Burglary", format: "EASI" },
      { field: "CARTHEFT", label: "Car Theft", format: "EASI" },
      { field: "LARCENY", label: "Larceny", format: "EASI" },
      { field: "MURDERS", label: "Murders", format: "EASI" },
      { field: "RAPE", label: "Rape", format: "EASI" },
      { field: "ROBBERY", label: "Robbery", format: "EASI" },
      { field: "TOTCRIME", label: "Total Crime", format: "EASI" },
    ],
    title: "EASI Crime",
  },
  "crime-block": {
    attributes: [
      { field: "ASSAULT", label: "Assault", format: "EASI" },
      { field: "BURGLARY", label: "Burglary", format: "EASI" },
      { field: "CARTHEFT", label: "Car Theft", format: "EASI" },
      { field: "LARCENY", label: "Larceny", format: "EASI" },
      { field: "MURDERS", label: "Murders", format: "EASI" },
      { field: "RAPE", label: "Rape", format: "EASI" },
      { field: "ROBBERY", label: "Robbery", format: "EASI" },
      { field: "TOTCRIME", label: "Total Crime", format: "EASI" },
    ],
    title: "EASI Crime",
  },
  "usgs-prob-mmi": {
    title: "USGS Probabilistic MMI VI",
    attributes: [
      { field: "high_cont", label: "Probability (%)", format: "DECIMALS_1" },
      { field: "low_cont", label: "Low Contour (%)", format: "DECIMALS_1" },
      { field: "range_cont", label: "Range", format: "DEFAULT" },
    ],
  },
  "florida-sinkholes": {
    title: "FL Subsidence Incident",
    attributes: [
      { field: "REF_NUM", label: "Reference Number", format: "DEFAULT" },
      { field: "EVENT_DATE", label: "Event Date", format: "DEFAULT" },
      { field: "DATE_REV", label: "Date Revised", format: "DEFAULT" },
      { field: "TRUE_SINK", label: "Verified Sinkhole", format: "DEFAULT" },
      { field: "COUNTY", label: "County", format: "DEFAULT" },
      { field: "OCITY", label: "City", format: "DEFAULT" },
      { field: "LATDD", label: "Latitude", format: "DECIMALS_2" },
      { field: "LONGDD", label: "Longitude", format: "DECIMALS_2" },
      { field: "SINLNGTH", label: "Length (ft)", format: "DECIMALS_1" },
      { field: "SINWIDTH", label: "Width (ft)", format: "DECIMALS_1" },
      { field: "SINDEPTH", label: "Depth (ft)", format: "DECIMALS_1" },
      { field: "SLOPE", label: "Slope of Sides", format: "DEFAULT" },
      { field: "WATSIN", label: "Water Visible", format: "DEFAULT" },
      { field: "LIMVIS", label: "Limestone Visible", format: "DEFAULT" },
      { field: "CAVVIS", label: "Cave Visible", format: "DEFAULT" },
      { field: "PROPDAM", label: "Property Damage", format: "DEFAULT" },
      { field: "REPAIR_S", label: "Repair Status", format: "DEFAULT" },
      { field: "RPT_SOURCE", label: "Report Source", format: "DEFAULT" },
      { field: "RPT_NAME", label: "Report Name", format: "DEFAULT" },
      { field: "DRAINSTR", label: "Drainage Structures", format: "DEFAULT" },
      { field: "SOILTYPE", label: "Soil Type", format: "DEFAULT" },
      { field: "COMMENTS", label: "Comments", format: "DEFAULT" },
      { field: "COMMENTS_2", label: "Additional Comments", format: "DEFAULT" },
    ],
  },
  "noaa-spc-tornadoes": {
    title: "NOAA SPC Tornado",
    attributes: [
      { field: "date", label: "Date", format: "DEFAULT" },
      { field: "mag", label: "EF Rating", format: "DEFAULT" },
      { field: "st", label: "State", format: "DEFAULT" },
      { field: "inj", label: "Injuries", format: "INTEGER" },
      { field: "fat", label: "Fatalities", format: "INTEGER" },
      { field: "len", label: "Path Length (mi)", format: "DECIMALS_1" },
      { field: "wid", label: "Path Width (yd)", format: "INTEGER" },
    ],
  },
  "noaa-spc-hail": {
    title: "NOAA SPC Hail",
    attributes: [
      { field: "date", label: "Date", format: "DEFAULT" },
      { field: "mag", label: "Size (inches)", format: "DECIMALS_2" },
      { field: "st", label: "State", format: "DEFAULT" },
      { field: "inj", label: "Injuries", format: "INTEGER" },
      { field: "fat", label: "Fatalities", format: "INTEGER" },
    ],
  },
  "noaa-spc-wind": {
    title: "NOAA SPC Wind",
    attributes: [
      { field: "date", label: "Date", format: "DEFAULT" },
      { field: "mag", label: "Speed (MPH)", format: "INTEGER" },
      { field: "st", label: "State", format: "DEFAULT" },
      { field: "inj", label: "Injuries", format: "INTEGER" },
      { field: "fat", label: "Fatalities", format: "INTEGER" },
    ],
  },
  "ufo-sightings": {
    title: "UFO Sighting",
    attributes: [
      { field: "Date_time", label: "Date/Time", format: "DEFAULT" },
      { field: "city", label: "City", format: "DEFAULT" },
      { field: "state_prov", label: "State", format: "DEFAULT" },
      { field: "country", label: "Country", format: "DEFAULT" },
      { field: "UFO_shape", label: "Shape", format: "DEFAULT" },
      { field: "described_", label: "Duration", format: "DEFAULT" },
    ],
  },
  geocoding: {
    title: `- Geocoding Result`,
    attributes: [
      { field: "address", label: "Address", format: "DEFAULT" },
      { field: "latlng", label: "Lat/Lng", format: "DEFAULT" },
      { field: "confidence", label: "Confidence", format: "DEFAULT" },
      { field: "precision", label: "Precision", format: "DEFAULT" },
      { field: "source", label: "Source", format: "DEFAULT" },
      { field: "postal_code", label: "Postal Code", format: "DEFAULT" },
      { field: "city", label: "City", format: "DEFAULT" },
      { field: "state", label: "State", format: "DEFAULT" },
      { field: "country", label: "Country", format: "DEFAULT" },
    ],
  },
};

/**
 * Default color range for heatmap layers
 * Gradient from dark navy → cyan → green → yellow → orange → red
 */
export const DEFAULT_HEATMAP_COLOR_RANGE: Color[] = [
  [13, 13, 64, 255], // Dark navy
  [14, 26, 102, 255],
  [15, 45, 140, 255],
  [16, 179, 179, 255], // Cyan
  [17, 204, 170, 255],
  [18, 224, 160, 255],
  [19, 241, 0, 255], // Green
  [64, 255, 0, 255],
  [128, 255, 0, 255],
  [255, 255, 0, 255], // Yellow
  [255, 213, 0, 255],
  [255, 170, 0, 255],
  [255, 128, 0, 255],
  [255, 85, 0, 255],
  [255, 85, 0, 255],
  [255, 42, 0, 255],
  [255, 42, 0, 255],
  [255, 20, 0, 255],
  [255, 20, 0, 255],
  [255, 0, 0, 255],
  [255, 0, 0, 255],
  [255, 0, 0, 255], // Red
];

export const DEFAULT_MODE3D_MAXIMUM_ZOOM = 10;

export const PING_3D_ZOOM_SCALES = {
  10: { elevation: 40, radius: 1400 },
  9: { elevation: 75, radius: 2200 },
  8: { elevation: 100, radius: 3000 },
  7: { elevation: 200, radius: 5200 },
  6: { elevation: 400, radius: 8400 },
  5: { elevation: 500, radius: 12000 },
};
